
import { RockAnalysis, HistoryItem } from "../types";

const STORAGE_KEY = "stone_master_history_v1";
const MAX_HISTORY_ITEMS = 50; // Keep local storage lightweight
const THUMBNAIL_WIDTH = 300; // Small enough for storage, big enough for context

// Helper: Compress image to small thumbnail
const createThumbnail = async (base64Image: string): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = THUMBNAIL_WIDTH / img.width;
      canvas.width = THUMBNAIL_WIDTH;
      canvas.height = img.height * scale;

      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        // Heavy compression (0.6 quality JPEG)
        resolve(canvas.toDataURL("image/jpeg", 0.6));
      } else {
        resolve(base64Image); // Fallback (shouldn't happen)
      }
    };
    img.src = base64Image;
  });
};

export const saveToHistory = async (analysis: RockAnalysis, fullImage: string): Promise<HistoryItem[]> => {
  try {
    const thumbnail = await createThumbnail(fullImage);
    
    const newItem: HistoryItem = {
      id: Date.now().toString(),
      timestamp: Date.now(),
      data: analysis,
      thumbnail: thumbnail,
    };

    const currentHistory = getHistory();
    // Add to top, remove duplicates if any, limit size
    const updatedHistory = [newItem, ...currentHistory].slice(0, MAX_HISTORY_ITEMS);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedHistory));
    return updatedHistory;
  } catch (e) {
    console.error("Failed to save history (likely storage quota exceeded):", e);
    // Even if saving fails, return current history so UI doesn't break
    return getHistory();
  }
};

export const getHistory = (): HistoryItem[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (e) {
    console.error("Failed to load history:", e);
    return [];
  }
};

export const clearHistory = (): void => {
  localStorage.removeItem(STORAGE_KEY);
};

export const deleteHistoryItem = (id: string): HistoryItem[] => {
  const current = getHistory();
  const updated = current.filter(item => item.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  return updated;
};
